import React, {useContext, useEffect, useRef, useState} from "react";
import {Anchor, Box, DateInput, FormField, Paragraph, ResponsiveContext, Select, Text, TextArea} from "grommet";
import Form from "../../Form";
import HiddenFormField from "../../HiddenFormField";
import SelectRecipient from "../draft-invoices/SelectRecipient";
import CurrencyInput from "../../CurrencyInput";
import {handleErrors} from "../../../utils/errors";
import StatusMessage from "../../StatusMessage";
import { debounce } from "../../../utils/debounce";
import ReconciliationModal from "./ReconciliationModal";
import InvoiceTag from "../../InvoiceTag";
import { NotificationsContext } from "../../NotificationsProvider";
import useSWR from "swr";
import Loading from "../../Loading";
import {
    faXmarkLarge as IconCloseBold
} from '@fortawesome/pro-regular-svg-icons';
import {
    faMoneyCheckDollar as IconPayment,
    faFileMinus as IconCredit,
    faFilePlus as IconDebit
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "../../FontAwesomeIcon";
import Button from "../../Button";
import {format} from 'date-fns';

const TRANSACTION_TYPES = [
    {
        value: 'external-payment',
        label: 'External payment',
        description: 'Record a payment received outside of Streamlined',
        icon: IconPayment
    },
    {
        value: 'credit-note',
        label: 'Credit note',
        description: 'Give credit or adjust an invoice balance down',
        icon: IconCredit
    },
    {
        value: 'debit-note',
        label: 'Debit note',
        description: 'Add charges or adjust an invoice balance up',
        icon: IconDebit
    }
];

const RecordJournalEntry = ({
    entryType, onSubmit, onCancel, defaultCustomer, defaultInvoice, defaultCustomerIsGroup, customerId, onTypeChange
}) => {
    const [recipientId, setRecipientId] = useState(customerId || defaultCustomer?.id);
    const defaultAmount = () => {
        const balance = defaultInvoice?.balance?.amount || 0;
        // Debit Note: If negative balance, set default amount to available credit. Else, set to 0.
        if(entryType === 'debit-note'){
            if(balance < 0 ){
                return(Math.abs(balance));
            } else{
                return 0
            }
        // Credit note: If negative balance, set default to 0. Else set to the remaining balance.
        }else if (entryType === 'credit-note') {
            if(balance < 0){
                return 0
            } else{
                return Math.abs(balance)
            }
        // External Payment: If negative balance, set default to 0. Else set to remaining balance.
        } else if (entryType === 'external-payment'){
            if(balance < 0) {
                return 0;
            } else {
                return balance;
            }
        // Fallback
        } else {
            return Math.abs(balance);
        }
    }
    const [formValues, setFormValues] = useState({
        created_at: new Date().toISOString(),
        entry_type: entryType,
        apply_payment: [],
        ...(defaultCustomer ? {recipient_id: customerId || defaultCustomer?.id } : {}),
        ...(defaultInvoice ? {
            amount: defaultAmount()
        } : {})
    });

    const [invoices, setInvoices] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState(defaultInvoice ? [{invoice_id: defaultInvoice?.invoice.id, amount: defaultInvoice?.invoice.amount}] : []);
    const [formError, setFormError] = useState();
    const [isGroup, setIsGroup] = useState(defaultCustomerIsGroup);
    const [showSelectInvoicesModal, setShowSelectInvoicesModal] = useState(false);
    const notificationCtx = useContext(NotificationsContext);
    const typeSelectRef = useRef(null);

    // Focus transaction type select on mount
    useEffect(() => {
        typeSelectRef.current?.focus();
    }, []);

    // Sync entryType prop with formValues when it changes
    useEffect(() => {
        setFormValues(prev => ({
            ...prev,
            entry_type: entryType
        }));
    }, [entryType]);

    useEffect(() => {
        setInvoices([]);
        // Don't fetch invoices if recipient isn't set
        if(!recipientId){
            return;
        }
        fetch(`/api/invoices/select?status=all&customer_id=${recipientId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        }).then(handleErrors)
            .then((data) => {
                setInvoices(data?.invoices);
            })
            .catch((err) => {
                setFormError(err);
            });
    }, [recipientId]);

    useEffect(() => {
        const hasInvoiceSelected = selectedInvoices && selectedInvoices.length > 0;
        setFormValues({
            ...formValues,
            apply_payment: hasInvoiceSelected ? selectedInvoices : [] // Use rest to apply credit/debit
        });
    }, [selectedInvoices]);

    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(notificationCtx.showNotification);

    const { data: invoicesToSelectedCustomer } = useSWR(
        recipientId && `/api/invoices?customer_id=${recipientId}&include_group_invoices=1`,
        fetcher,
        {
            revalidateOnFocus: false
        },
    );

    const renderSelectInvoiceComponent = () => {
        if (selectedInvoices && selectedInvoices.length > 0) {
            return ( <SelectedInvoiceInput
                invoices={selectedInvoices}
                onClear={() => setSelectedInvoices([])}
                onEdit={() => setShowSelectInvoicesModal(true)} />);
        }
        return (
            <Anchor
                margin={{top: 'xsmall'}}
                onClick={()=> setShowSelectInvoicesModal(true)}>
                    + Add invoices
            </Anchor>
        );
    }


    return (
        <ResponsiveContext.Consumer>
            { (size) => (
                <Box>
                    <Form
                        loggingComponentName="record_journal_entry"
                        onReset={onCancel}
                        onSubmit={(event) => {
                            if (recipientId && invoices && event.value.apply_payment.length === 0) {
                                setFormError({message: 'You must select an invoice to record a transaction'});
                                return;
                            }
                            debounce(onSubmit,300)(event)
                        }}
                        value={formValues}
                        onChange={(nextValue, {touched}) => {
                            let nextRecipientId = nextValue.recipient_id || '';
                            if (touched.hasOwnProperty('recipient_id') && typeof nextValue.recipient_id === 'object') {
                                nextRecipientId = nextValue.recipient_id.value
                            }
                            if (recipientId !== nextRecipientId) {
                                setSelectedInvoices(undefined);
                            }
                            setRecipientId(nextRecipientId);
                            setFormValues({
                                ...nextValue,
                                recipient_id: nextRecipientId,
                                ...(recipientId && (recipientId !== nextRecipientId) ?
                                    { apply_payment: []} : {}) // reset selected invoice when merchant changes selected customer
                            });
                        }}
                        editOnly
                        sectionProps={{
                            margin: 'none',
                            border: false,
                            width: (size === 'small' ? 'auto' : 'medium'),
                            pad: 'none',
                            headerProps: {
                                border: false,
                            },
                        }}
                    >
                        <Box margin={{ bottom: 'small' }}>
                            <Text weight={500} size="small">Transaction type</Text>
                            <Description type={entryType} />
                            <Select
                                ref={typeSelectRef}
                                options={TRANSACTION_TYPES}
                                labelKey="label"
                                valueKey={{ key: 'value', reduce: true }}
                                value={entryType}
                                onChange={({ value }) => {
                                    onTypeChange && onTypeChange(value);
                                }}
                            />
                        </Box>
                        <HiddenFormField name="entry_type" value={entryType} />
                        {formError && (<StatusMessage apiError={formError} />)}
                        <FormField
                            label="Amount"
                            name="amount"
                            component={CurrencyInput}
                            inputCurrency={defaultInvoice?.currency}
                            onChange={() => {
                                if (selectedInvoices && selectedInvoices.length > 1) {
                                    setSelectedInvoices([])
                                }
                            }}
                            required
                        />
                        {entryType === 'external-payment' && (
                            <FormField
                                label="Payment received by"
                                component={Select}
                                options={[
                                    { label: 'Bank transfer', value: 'ach_credit' },
                                    { label: 'Cash', value: 'cash' },
                                    { label: 'Check', value: 'check' },
                                    { label: 'Credit / Debit card', value: 'card' },
                                ]}
                                labelKey="label"
                                valueKey={{ key: 'value', reduce: true }}
                                name="payment_source"
                                required
                            />
                        )}
                        <FormField
                            name="created_at"
                            label="Transaction date"
                            format="mm/dd/yyyy"
                            showAdjacentDays={false}
                            component={DateInput}
                            calendarProps={{
                                alignSelf: "center",
                                daysOfWeek: true,
                                bounds: ['2020-01-01', format(new Date(), 'yyyy-MM-dd')],
                                animate: false,
                                showAdjacentDays: "trim"
                            }}
                            dropProps={{
                                stretch: false,
                                border: true,
                                round: "xsmall",
                                pad: "small"
                            }}
                            validate={(date) => {
                                const today = new Date();
                                if (date && (Date.parse(date) > today)) return 'Please enter a day that is in the past.';
                                return undefined;
                            }}
                            required
                        />
                        <FormField
                            label="Customer"
                            name="recipient_id"
                            validate={(recipient) => {
                                if (!recipient) return 'You must search for a customer and select an option.';
                            }}
                            defaultRecipient={defaultCustomer}
                            component={SelectRecipient}
                            onRecipientIsGroupChange={setIsGroup} // This gets called when user select a customer group
                        />
                        {recipientId && !invoicesToSelectedCustomer && (
                            <Loading plain/>
                        )}
                        {recipientId && invoicesToSelectedCustomer && invoicesToSelectedCustomer.invoices.length === 0 && (
                            <Text color="status-error" size="small">You must select a customer who has received an invoice from you.</Text>
                        )}
                        {recipientId && invoices && invoicesToSelectedCustomer && invoicesToSelectedCustomer.invoices.length > 0 && (
                            <Box>
                                <Box margin={{top: 'small'}}>
                                    <Text weight={600} size="small" color="text-strong">
                                        Invoice{entryType === 'external-payment' ? 's' : null}*
                                    </Text>
                                </Box>
                                {renderSelectInvoiceComponent()}
                            </Box>
                        )}
                        <FormField
                            label="Memo"
                            name="memo_content"
                            component={TextArea}
                        />
                    </Form>
                    {showSelectInvoicesModal && (
                        <ReconciliationModal
                            journalEntryType={entryType}
                            makeCallToLinkInvoice={false}
                            onClose={() => setShowSelectInvoicesModal(false)}
                            recipientId={recipientId}
                            onSubmit={data => {
                                // data is a list of invoice object, want just a list of id instead
                                setSelectedInvoices(data);
                                setShowSelectInvoicesModal(false);
                            }}
                            amountToMatch={formValues?.amount}
                            ctaText="Confirm"
                            singleInvoiceOnly={entryType !== "external-payment"}
                        />
                    )}
                </Box>
            )}
        </ResponsiveContext.Consumer>
    );
};

const SelectedInvoiceInput = ({invoices, onClear, onEdit}) => {
    return invoices ? (
        <Box round="xsmall" border={{color: 'border-strong'}} pad="xsmall" justify="between" align="center" direction="row" margin={{vertical: 'xsmall'}}>
            <Box wrap direction="row" gap="xsmall" align="center">
                {invoices.map((invoice) => (
                    <InvoiceTag id={invoice.invoice_id} key={invoice.invoice_id} />
                ))}
            </Box>
            <Button
                event={{section: 'record_journal_ntry', element: 'clear'}}
                label={(<FontAwesomeIcon SelectedIcon={IconCloseBold} size="xxxsmall" color="text" />)}
                hoverIndicator
                onClick={onClear} />
        </Box>
    ) : (<></>);
}

const Description = ({ type }) => {
    let description = '';

    if (type === 'external-payment') {
        description = "Record a payment received outside of Streamlined.";
    }
    if (type === 'credit-note') {
        description = "Give a customer credit or reduce what they owe.";
    }
    if (type === 'debit-note') {
        description = "Add extra charges or increase what a customer owes.";
    }

    return <Text size="small" color="text-weak" margin={{ bottom: 'xsmall' }}>{description}</Text>;
};

export default RecordJournalEntry;
